import React from "react";
import { motion } from "framer-motion";
import { Users, Sparkles, MapPin, MessageCircle, Mail, ArrowRight } from "lucide-react";
import { useI18n } from "../lib/i18n";
import useTitle from "../lib/useTitle";
import { CONTACT } from "../lib/constants";

const heroImg = "https://images.unsplash.com/photo-1661622960824-86b510e68449?w=2000&q=80";

const groupTypes = [
  { icon: Users, en: "Private groups", fr: "Groupes privés", descEn: "Friends, families, retreats — book a whole guest house or buy out the hostel.", descFr: "Amis, familles, retraites — privatise une maison d’hôte ou tout le hostel." },
  { icon: Sparkles, en: "Creator tours", fr: "Tours créateurs", descEn: "Curated 4-7 day stays designed for content makers — beach, desert, Berber villages.", descFr: "Séjours sur-mesure 4-7 jours pour créateurs — plage, désert, villages berbères." },
  { icon: MapPin, en: "South Tunisia tour", fr: "Tour Sud tunisien", descEn: "Multi-stop adventure across Djerba, Matmata, Tataouine, and the Sahara dunes.", descFr: "Aventure multi-étapes : Djerba, Matmata, Tataouine, dunes du Sahara." },
];

export default function Tours() {
  const { t, lang } = useI18n();
  useTitle(t.legal.tour.title);
  const isFr = lang === "fr";

  return (
    <main data-testid="tours-page">
      {/* HERO */}
      <section className="relative min-h-[60vh] flex items-end overflow-hidden bg-wostel-navy">
        <img src={heroImg} alt="" className="absolute inset-0 w-full h-full object-cover opacity-60"/>
        <div className="absolute inset-0 bg-gradient-to-t from-wostel-navy via-wostel-navy/40 to-transparent"/>
        <div className="container-x relative z-10 pb-16 pt-32 text-sand-50">
          <span className="eyebrow text-wostel-sun">{t.legal.tour.title}</span>
          <h1 className="font-extrabold text-5xl sm:text-6xl lg:text-7xl tracking-tight mt-5 max-w-3xl leading-[0.95]">
            {isFr ? "Voyage en groupe ?" : "Travelling with a crew?"} <span className="text-wostel-sun">{isFr ? "On gère." : "We got you."}</span>
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-sand-50/85 leading-relaxed">{t.legal.tour.body}</p>
        </div>
      </section>

      {/* CARDS */}
      <section className="container-x py-20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {groupTypes.map((g, i) => (
            <motion.article
              key={i}
              data-testid={`tour-type-${i}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="bg-white rounded-3xl p-8 border border-sand-300/60 hover:shadow-2xl hover:-translate-y-1 transition-all"
            >
              <div className="w-14 h-14 rounded-2xl bg-wostel-cyan/10 text-wostel-cyan flex items-center justify-center mb-6">
                <g.icon size={22}/>
              </div>
              <h3 className="font-extrabold text-xl mb-3 tracking-tight">{isFr ? g.fr : g.en}</h3>
              <p className="text-sm text-wostel-ink/70 leading-relaxed">{isFr ? g.descFr : g.descEn}</p>
            </motion.article>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="container-x pb-24">
        <div className="bg-wostel-ink text-sand-50 rounded-3xl p-10 sm:p-16 relative overflow-hidden">
          <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-wostel-sun/20 blur-3xl"/>
          <div className="absolute -bottom-20 -left-20 w-72 h-72 rounded-full bg-wostel-cyan/20 blur-3xl"/>
          <div className="relative max-w-3xl">
            <span className="eyebrow text-wostel-sun">{isFr ? "Construisons ton voyage" : "Let’s build your trip"}</span>
            <h2 className="font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-4 leading-[1]">
              {isFr ? "Dis-nous ce dont tu rêves," : "Tell us what you dream,"}<br/>
              <span className="text-wostel-sun">{isFr ? "on construit le reste." : "we build the rest."}</span>
            </h2>
            <p className="mt-6 text-sand-50/80 leading-relaxed max-w-xl">
              {isFr ? "Réponse sous 24h. Devis transparent. Locaux, dans tous les sens du terme." : "Reply within 24h. Transparent pricing. Local, in every sense."}
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <a href={CONTACT.whatsapp} target="_blank" rel="noreferrer" data-testid="tours-whatsapp"
                 className="btn-pill bg-wostel-sun text-wostel-ink hover:brightness-95">
                <MessageCircle size={16}/> WhatsApp
              </a>
              <a href={`mailto:${CONTACT.email}`} data-testid="tours-email"
                 className="btn-pill bg-transparent border border-sand-50/40 text-sand-50 hover:bg-sand-50/10">
                <Mail size={16}/> {CONTACT.email}
              </a>
              <a href="/creators" data-testid="tours-creator-link"
                 className="btn-pill bg-transparent border border-sand-50/40 text-sand-50 hover:bg-sand-50/10">
                {isFr ? "Tu es créateur ?" : "Are you a creator?"} <ArrowRight size={14}/>
              </a>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
